import { uploadFileToS3 } from "api/media-library/upload-file-to-s3";
import {
  clearLazyBlobRef,
  getVideoPreviewImageWithRetry,
  getOrCreateVideoBlob,
  revokeBlobUrl,
} from "components/Posts/PostForm/components/VideoCoverSelector/utils";
import { logVideoCoverBreadcrumb } from "components/Posts/PostForm/components/VideoCoverSelector/observability";
import {
  VIDEO_COVER_TIMESTAMPS,
  VIDEO_COVER_TYPES,
} from "constants/VideoCoverConstants";
import { convertCustomerUploadsS3ToCloudFront } from "libs/media-files";
import { isNewPostId } from "utils/posts";
import { isImagePreviewUrl } from "utils/media";
import { Post } from "./types";
import { allowedSelectCoverPlatforms } from "./const";
import {
  mergePlatformPostFields,
  normalizePostMediaShape,
} from "./format-post";

const selectableCoverPlatforms =
  allowedSelectCoverPlatforms as readonly string[];

function hasPersistedPostId(post: Post) {
  return Boolean(post._id && !isNewPostId(post._id));
}

function buildVideoCoverFileName(post: Post) {
  const postIdPrefix = hasPersistedPostId(post) ? post._id : "unsaved-post";

  return `${postIdPrefix}-video_cover_${Date.now()}.png`;
}

export { mergePlatformPostFields, normalizePostMediaShape };

export function syncCoverToMediaFiles(post: Post): Post {
  const hasCoverPlatform = post.platforms?.some((platform) =>
    selectableCoverPlatforms.includes(String(platform))
  );

  if (
    !hasCoverPlatform ||
    !post.thumbnailUrl ||
    !Array.isArray(post.mediaFiles) ||
    post.mediaFiles.length === 0
  ) {
    return post;
  }

  return {
    ...post,
    mediaFiles: [
      { ...post.mediaFiles[0], thumbnailUrl: post.thumbnailUrl },
      ...post.mediaFiles.slice(1),
    ],
  };
}

export function creatingPostNeedUploadCover(post: Post) {
  return (
    hasExplicitCoverPreviewToUpload(post) ||
    (!hasPersistedPostId(post) && shouldRegenerateFrameSelectedCover(post))
  );
}

export function editingPostNeedUploadCover(
  currentPost: Post,
  previousPost: Post
) {
  const selectedCoverChanged =
    currentPost.videoCoverImageTimestamp !==
      previousPost?.videoCoverImageTimestamp ||
    currentPost.videoCoverType !== previousPost?.videoCoverType ||
    (currentPost.videoCoverType === VIDEO_COVER_TYPES.UPLOADED &&
      currentPost.thumbnailUrl !== previousPost?.thumbnailUrl);

  return hasCoverPreviewToUpload(currentPost) && selectedCoverChanged;
}

function hasCoverPreviewToUpload(post: Post) {
  return (
    hasExplicitCoverPreviewToUpload(post) ||
    shouldRegenerateFrameSelectedCover(post)
  );
}

function hasExplicitCoverPreviewToUpload(post: Post) {
  return Boolean(post?.videoCoverPreview) && isUserSelectedCover(post);
}

function shouldRegenerateFrameSelectedCover(post: Post) {
  return (
    !post.videoCoverPreview &&
    (post.videoCoverType === VIDEO_COVER_TYPES.FRAME_SELECTED ||
      (typeof post.videoCoverImageTimestamp === "number" &&
        post.videoCoverImageTimestamp > VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME))
  );
}

function isUserSelectedCover(post: Post) {
  return (
    post.videoCoverType === VIDEO_COVER_TYPES.UPLOADED ||
    post.videoCoverType === VIDEO_COVER_TYPES.FRAME_SELECTED ||
    post.videoCoverImageTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER ||
    (typeof post.videoCoverImageTimestamp === "number" &&
      post.videoCoverImageTimestamp > VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME)
  );
}

function getFrameSelectedSourceUrls(post: Post) {
  const firstMedia = post.mediaFiles?.[0];
  const lowQualityVideoUrl = (
    firstMedia as { lowQualityVideo?: { url?: string } } | undefined
  )?.lowQualityVideo?.url;
  const candidateUrls = [
    lowQualityVideoUrl,
    firstMedia?.url,
    post.url,
    firstMedia?.originalUrl,
    firstMedia?.origUrl,
    post.originalUrl,
    post.origUrl,
  ].filter(Boolean) as string[];

  return [...new Set(candidateUrls.map(convertCustomerUploadsS3ToCloudFront))];
}

function getFrameSelectedCoverFrameUrl(post: Post) {
  const timestampInMs = post.videoCoverImageTimestamp;
  if (typeof timestampInMs !== "number") {
    return null;
  }

  const mediaWithCoverFrames = post.mediaFiles?.[0] as
    { coverFrames?: { timestampMs: number; url: string }[] } | undefined;
  const frame = mediaWithCoverFrames?.coverFrames?.find(
    (candidate) => candidate.timestampMs === timestampInMs
  );
  return frame?.url ? convertCustomerUploadsS3ToCloudFront(frame.url) : null;
}

function getFrameSelectedTimestampSeconds(post: Post) {
  const timestampInMs = post.videoCoverImageTimestamp;

  if (
    typeof timestampInMs !== "number" ||
    timestampInMs < VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME ||
    (timestampInMs === VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME &&
      post.videoCoverType !== VIDEO_COVER_TYPES.FRAME_SELECTED)
  ) {
    return null;
  }

  return timestampInMs / 1000;
}

async function regenerateFrameSelectedPreview(post: Post) {
  const timestampSeconds = getFrameSelectedTimestampSeconds(post);

  if (timestampSeconds === null) {
    return null;
  }

  const sourceUrls = getFrameSelectedSourceUrls(post);

  for (const sourceUrl of sourceUrls) {
    try {
      return await getVideoPreviewImageWithRetry(sourceUrl, timestampSeconds);
    } catch {
      // Try the next source URL.
    }
  }

  const blobSourceUrl = sourceUrls[0];

  if (blobSourceUrl) {
    const lazyBlobRef = {
      current: null,
    } as Parameters<typeof getOrCreateVideoBlob>[1];

    try {
      const { blobUrl } = await getOrCreateVideoBlob(
        blobSourceUrl,
        lazyBlobRef
      );
      return await getVideoPreviewImageWithRetry(blobUrl, timestampSeconds);
    } finally {
      clearLazyBlobRef(lazyBlobRef);
    }
  }

  return null;
}

async function getCoverBlobToUpload(post: Post) {
  let previewUrl = post.videoCoverPreview;
  let generatedPreviewUrl: string | null = null;

  if (!previewUrl && shouldRegenerateFrameSelectedCover(post)) {
    previewUrl = getFrameSelectedCoverFrameUrl(post);
    if (!previewUrl) {
      generatedPreviewUrl = await regenerateFrameSelectedPreview(post);
      previewUrl = generatedPreviewUrl;
    }
  }

  if (!previewUrl) {
    return null;
  }

  const fetchPreviewBlob = async (url: string) => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cover preview: ${response.status} ${response.statusText}`
      );
    }

    return response.blob();
  };

  try {
    try {
      return await fetchPreviewBlob(previewUrl);
    } catch (error) {
      logVideoCoverBreadcrumb("video_cover.save.preview_fetch.failure", {
        videoCoverType: post.videoCoverType,
        videoCoverImageTimestamp: post.videoCoverImageTimestamp,
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      if (
        post.videoCoverType !== VIDEO_COVER_TYPES.FRAME_SELECTED ||
        generatedPreviewUrl
      ) {
        throw error;
      }

      const coverFrameUrl = getFrameSelectedCoverFrameUrl(post);
      if (coverFrameUrl && coverFrameUrl !== previewUrl) {
        try {
          return await fetchPreviewBlob(coverFrameUrl);
        } catch (coverFrameError) {
          logVideoCoverBreadcrumb(
            "video_cover.save.cover_frame_fetch.failure",
            {
              videoCoverImageTimestamp: post.videoCoverImageTimestamp,
              errorMessage:
                coverFrameError instanceof Error
                  ? coverFrameError.message
                  : String(coverFrameError),
            }
          );
        }
      }

      try {
        generatedPreviewUrl = await regenerateFrameSelectedPreview(post);
      } catch (regenerationError) {
        logVideoCoverBreadcrumb("video_cover.save.regenerate.failure", {
          videoCoverImageTimestamp: post.videoCoverImageTimestamp,
          errorMessage:
            regenerationError instanceof Error
              ? regenerationError.message
              : String(regenerationError),
        });
        throw regenerationError;
      }
      if (!generatedPreviewUrl) {
        logVideoCoverBreadcrumb("video_cover.save.regenerate.failure", {
          videoCoverImageTimestamp: post.videoCoverImageTimestamp,
          errorMessage: "No frame preview could be generated",
        });
        throw error;
      }

      logVideoCoverBreadcrumb("video_cover.save.regenerate.success", {
        videoCoverImageTimestamp: post.videoCoverImageTimestamp,
      });
      try {
        return await fetchPreviewBlob(generatedPreviewUrl);
      } catch (regeneratedPreviewError) {
        logVideoCoverBreadcrumb(
          "video_cover.save.regenerated_preview_fetch.failure",
          {
            videoCoverImageTimestamp: post.videoCoverImageTimestamp,
            errorMessage:
              regeneratedPreviewError instanceof Error
                ? regeneratedPreviewError.message
                : String(regeneratedPreviewError),
          }
        );
        throw regeneratedPreviewError;
      }
    }
  } finally {
    if (generatedPreviewUrl) {
      revokeBlobUrl(generatedPreviewUrl);
    }
  }
}

function getDurableMediaLibraryCoverUrl(post: Post) {
  if (
    post.videoCoverType !== VIDEO_COVER_TYPES.UPLOADED ||
    post.videoCoverPreviewSource !== "media_library" ||
    !post.videoCoverPreview
  ) {
    return null;
  }

  const normalizedUrl = convertCustomerUploadsS3ToCloudFront(
    post.videoCoverPreview
  );
  try {
    const parsedUrl = new URL(normalizedUrl);
    if (
      parsedUrl.protocol !== "https:" ||
      parsedUrl.hostname !== "customer-uploads.skedsocial.com" ||
      parsedUrl.search ||
      !isImagePreviewUrl(normalizedUrl)
    ) {
      return null;
    }
  } catch {
    return null;
  }

  return normalizedUrl;
}

export async function getUpdateThumbnail(currentPost: Post) {
  const durableMediaLibraryCoverUrl =
    getDurableMediaLibraryCoverUrl(currentPost);
  if (durableMediaLibraryCoverUrl) {
    logVideoCoverBreadcrumb("video_cover.save.media_library_url.reused", {
      videoCoverType: currentPost.videoCoverType,
    });
    return durableMediaLibraryCoverUrl;
  }

  try {
    const blob = await getCoverBlobToUpload(currentPost);

    if (!blob) {
      if (isUserSelectedCover(currentPost)) {
        throw new Error(
          "We couldn't save your selected video cover. Please try again."
        );
      }

      return currentPost?.thumbnailUrl;
    }

    return await uploadFileToS3({
      blob,
      fileName: buildVideoCoverFileName(currentPost),
    });
  } catch (error) {
    if (isUserSelectedCover(currentPost)) {
      throw new Error(
        "We couldn't save your selected video cover. Please try again.",
        { cause: error }
      );
    }

    return currentPost?.thumbnailUrl;
  }
}
